import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { db } from "@/lib/firebase";
import { collection, addDoc, getDocs, query, where, orderBy, serverTimestamp, Timestamp } from "firebase/firestore";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { LanguageSelector } from "@/components/ui/language-selector";
import {
  ArrowLeft,
  Briefcase,
  Save,
  Scale,
  Calculator,
  DollarSign,
  Plane,
  FileSearch,
  FileText,
  Clock,
  CheckCircle,
  Calendar,
} from "lucide-react";

// Mandate Template Types
interface TemplateMilestone {
  title: string;
  description: string;
  dayOffset: number;
  required: boolean;
}

interface ChecklistItem {
  id: string;
  title: string;
  description?: string;
  required: boolean;
  category?: string;
}

interface MandateTemplate {
  id: string;
  name: string;
  description: string;
  category: 'legal' | 'tax' | 'financial' | 'immigration' | 'due_diligence' | 'other';
  defaultDuration: number;
  defaultEstimatedHours?: number;
  defaultPriority: 'low' | 'medium' | 'high' | 'urgent';
  requiredDocuments: string[];
  deliverables: string[];
  defaultMilestones: TemplateMilestone[];
  checklist: ChecklistItem[];
}

// Mandate Templates based on PDF
const MANDATE_TEMPLATES: MandateTemplate[] = [
  {
    id: "legal-due-diligence",
    name: "M&A Legal Due Diligence",
    description: "Comprehensive legal review for business acquisition",
    category: 'legal',
    defaultDuration: 45,
    defaultEstimatedHours: 80,
    defaultPriority: 'high',
    requiredDocuments: [
      "Articles of Incorporation",
      "Shareholder Agreements",
      "Material Contracts",
      "Employment Agreements",
      "Intellectual Property Documentation",
      "Litigation History",
      "Compliance Records"
    ],
    deliverables: [
      "Legal Due Diligence Report",
      "Risk Assessment Matrix",
      "Recommended Deal Structure",
      "List of Required Remedies"
    ],
    defaultMilestones: [
      { title: "Document Collection", description: "Gather all required legal documents", dayOffset: 7, required: true },
      { title: "Corporate Structure Review", description: "Review corporate governance and structure", dayOffset: 14, required: true },
      { title: "Contract Analysis", description: "Review all material contracts and agreements", dayOffset: 21, required: true },
      { title: "Compliance Review", description: "Assess regulatory compliance status", dayOffset: 28, required: true },
      { title: "Draft Report", description: "Prepare preliminary findings report", dayOffset: 35, required: true },
      { title: "Final Report Delivery", description: "Submit comprehensive due diligence report", dayOffset: 45, required: true },
    ],
    checklist: [
      { id: "1", title: "Verify corporate good standing", required: true, category: "Corporate" },
      { id: "2", title: "Review cap table and ownership structure", required: true, category: "Corporate" },
      { id: "3", title: "Analyze material contracts", required: true, category: "Contracts" },
      { id: "4", title: "Review employment agreements", required: true, category: "Employment" },
      { id: "5", title: "Assess IP portfolio", required: true, category: "IP" },
      { id: "6", title: "Check litigation history", required: true, category: "Litigation" },
      { id: "7", title: "Verify regulatory compliance", required: true, category: "Compliance" },
      { id: "8", title: "Review insurance policies", required: false, category: "Risk" },
    ],
  },
  {
    id: "tax-compliance",
    name: "Tax Compliance Review",
    description: "Complete tax review for business acquisition",
    category: 'tax',
    defaultDuration: 30,
    defaultEstimatedHours: 40,
    defaultPriority: 'high',
    requiredDocuments: [
      "3 Years Tax Returns (Federal & State)",
      "Sales Tax Filings",
      "Payroll Tax Records",
      "Tax Notices and Correspondence",
      "Tax Provision Worksheets"
    ],
    deliverables: [
      "Tax Due Diligence Report",
      "Tax Risk Assessment",
      "Estimated Tax Liabilities",
      "Recommendations for Structure"
    ],
    defaultMilestones: [
      { title: "Tax Document Collection", description: "Gather all tax returns and filings", dayOffset: 7, required: true },
      { title: "Federal Tax Review", description: "Review federal tax compliance", dayOffset: 12, required: true },
      { title: "State & Local Tax Review", description: "Assess state and local tax obligations", dayOffset: 18, required: true },
      { title: "Tax Risk Assessment", description: "Identify potential tax exposures", dayOffset: 25, required: true },
      { title: "Final Report", description: "Deliver comprehensive tax report", dayOffset: 30, required: true },
    ],
    checklist: [
      { id: "1", title: "Review last 3 years federal returns", required: true, category: "Federal" },
      { id: "2", title: "Verify tax payment history", required: true, category: "Compliance" },
      { id: "3", title: "Check for outstanding audits", required: true, category: "Risk" },
      { id: "4", title: "Review sales tax compliance", required: true, category: "State" },
      { id: "5", title: "Assess payroll tax compliance", required: true, category: "Payroll" },
      { id: "6", title: "Identify tax credit eligibility", required: false, category: "Planning" },
    ],
  },
  {
    id: "financial-qoe",
    name: "Financial Due Diligence - Quality of Earnings",
    description: "In-depth financial analysis and earnings quality assessment",
    category: 'financial',
    defaultDuration: 30,
    defaultEstimatedHours: 60,
    defaultPriority: 'high',
    requiredDocuments: [
      "3 Years Audited Financial Statements",
      "Monthly Financial Statements (Current Year)",
      "General Ledger Detail",
      "Customer Contracts and Revenue Documentation",
      "Accounts Receivable Aging",
      "Inventory Records",
      "CapEx Schedule"
    ],
    deliverables: [
      "Quality of Earnings Report",
      "Normalized EBITDA Analysis",
      "Working Capital Analysis",
      "Financial Risk Assessment"
    ],
    defaultMilestones: [
      { title: "Financial Data Collection", description: "Gather all financial records", dayOffset: 5, required: true },
      { title: "Revenue Analysis", description: "Analyze revenue quality and sustainability", dayOffset: 12, required: true },
      { title: "EBITDA Normalization", description: "Calculate normalized EBITDA", dayOffset: 18, required: true },
      { title: "Working Capital Review", description: "Assess working capital requirements", dayOffset: 24, required: true },
      { title: "Final QoE Report", description: "Deliver complete financial analysis", dayOffset: 30, required: true },
    ],
    checklist: [
      { id: "1", title: "Verify revenue recognition policies", required: true, category: "Revenue" },
      { id: "2", title: "Analyze customer concentration", required: true, category: "Revenue" },
      { id: "3", title: "Review cost structure", required: true, category: "Costs" },
      { id: "4", title: "Identify one-time expenses", required: true, category: "EBITDA" },
      { id: "5", title: "Assess working capital trends", required: true, category: "Working Capital" },
      { id: "6", title: "Review CapEx requirements", required: true, category: "CapEx" },
    ],
  },
  {
    id: "eb5-visa",
    name: "Immigration - EB-5 Investor Visa",
    description: "EB-5 visa application and documentation",
    category: 'immigration',
    defaultDuration: 90,
    defaultEstimatedHours: 50,
    defaultPriority: 'medium',
    requiredDocuments: [
      "Source of Funds Documentation",
      "Business Plan",
      "Financial Projections",
      "Job Creation Evidence",
      "Personal Identification Documents",
      "Tax Returns (5 years)"
    ],
    deliverables: [
      "I-526 Petition Package",
      "Source of Funds Report",
      "Business Plan Review",
      "Supporting Documentation Compilation"
    ],
    defaultMilestones: [
      { title: "Initial Consultation", description: "Review eligibility and requirements", dayOffset: 3, required: true },
      { title: "Document Collection", description: "Gather all required documents", dayOffset: 30, required: true },
      { title: "Source of Funds Analysis", description: "Document and verify source of investment", dayOffset: 50, required: true },
      { title: "Business Plan Review", description: "Review and finalize business plan", dayOffset: 70, required: true },
      { title: "I-526 Petition Filing", description: "Complete and file I-526 petition", dayOffset: 90, required: true },
    ],
    checklist: [
      { id: "1", title: "Verify investment amount ($800K or $1.05M)", required: true, category: "Investment" },
      { id: "2", title: "Document lawful source of funds", required: true, category: "Source of Funds" },
      { id: "3", title: "Confirm job creation (10 jobs)", required: true, category: "Jobs" },
      { id: "4", title: "Select regional center or direct investment", required: true, category: "Structure" },
      { id: "5", title: "Prepare business plan", required: true, category: "Documentation" },
      { id: "6", title: "Complete Form I-526", required: true, category: "Filing" },
    ],
  },
];

const CATEGORY_ICONS = {
  legal: Scale,
  tax: Calculator,
  financial: DollarSign,
  immigration: Plane,
  due_diligence: FileSearch,
  other: FileText,
};

const translations = {
  en: {
    title: "Create New Mandate",
    subtitle: "Create a new mandate from template or custom",
    back: "Back to Dashboard",
    selectTemplate: "Select a Template",
    allTemplates: "All Templates",
    customMandate: "Custom Mandate",
    templateDetails: "Template Details",
    mandateDetails: "Mandate Details",
    mandateTitle: "Mandate Title",
    titlePlaceholder: "Enter mandate title",
    description: "Description",
    descriptionPlaceholder: "Describe the scope of work...",
    selectClient: "Select Client",
    noClients: "No clients available",
    addClientFirst: "Add a client first",
    mandateType: "Mandate Type",
    priority: "Priority",
    duration: "Duration (days)",
    estimatedHours: "Estimated Hours",
    hourlyRate: "Hourly Rate ($)",
    startDate: "Start Date",
    dueDate: "Due Date",
    legal: "Legal Services",
    tax: "Tax Advisory",
    financial: "Financial Analysis",
    immigration: "Immigration Law",
    due_diligence: "Due Diligence",
    other: "Other Services",
    low: "Low",
    medium: "Medium",
    high: "High",
    urgent: "Urgent",
    useTemplate: "Use This Template",
    requiredDocuments: "Required Documents",
    deliverables: "Key Deliverables",
    milestones: "Milestones",
    checklist: "Checklist",
    days: "days",
    hours: "hours",
    create: "Create Mandate",
    creating: "Creating...",
    success: "Mandate Created",
    successMessage: "Mandate has been created successfully",
    error: "Error",
    errorMessage: "Failed to create mandate",
    required: "This field is required",
    cancel: "Cancel",
  },
  fr: {
    title: "Créer un Nouveau Mandat",
    subtitle: "Créez un nouveau mandat à partir d'un modèle ou personnalisé",
    back: "Retour au Tableau de Bord",
    selectTemplate: "Sélectionner un Modèle",
    allTemplates: "Tous les Modèles",
    customMandate: "Mandat Personnalisé",
    templateDetails: "Détails du Modèle",
    mandateDetails: "Détails du Mandat",
    mandateTitle: "Titre du Mandat",
    titlePlaceholder: "Entrez le titre du mandat",
    description: "Description",
    descriptionPlaceholder: "Décrivez l'étendue des travaux...",
    selectClient: "Sélectionner un Client",
    noClients: "Aucun client disponible",
    addClientFirst: "Ajoutez d'abord un client",
    mandateType: "Type de Mandat",
    priority: "Priorité",
    duration: "Durée (jours)",
    estimatedHours: "Heures Estimées",
    hourlyRate: "Taux Horaire ($)",
    startDate: "Date de Début",
    dueDate: "Date d'Échéance",
    legal: "Services Juridiques",
    tax: "Conseil Fiscal",
    financial: "Analyse Financière",
    immigration: "Droit de l'Immigration",
    due_diligence: "Due Diligence",
    other: "Autres Services",
    low: "Basse",
    medium: "Moyenne",
    high: "Haute",
    urgent: "Urgente",
    useTemplate: "Utiliser ce Modèle",
    requiredDocuments: "Documents Requis",
    deliverables: "Livrables Clés",
    milestones: "Jalons",
    checklist: "Liste de Contrôle",
    days: "jours",
    hours: "heures",
    create: "Créer le Mandat",
    creating: "Création en cours...",
    success: "Mandat Créé",
    successMessage: "Le mandat a été créé avec succès",
    error: "Erreur",
    errorMessage: "Échec de la création du mandat",
    required: "Ce champ est requis",
    cancel: "Annuler",
  },
};

interface Client {
  id: string;
  name: string;
  email: string;
  company?: string;
  type: 'buyer' | 'seller' | 'broker';
}

export default function NewMandate() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const { toast } = useToast();
  const t = translations[language as keyof typeof translations] || translations.en;

  const [activeTab, setActiveTab] = useState("templates");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedTemplate, setSelectedTemplate] = useState<MandateTemplate | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingClients, setIsLoadingClients] = useState(true);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    clientId: "",
    type: "" as 'legal' | 'tax' | 'financial' | 'immigration' | 'due_diligence' | 'other' | '',
    priority: "medium" as 'low' | 'medium' | 'high' | 'urgent',
    duration: 30,
    estimatedHours: 40,
    hourlyRate: 250,
    startDate: new Date().toISOString().split('T')[0],
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (user) {
      loadClients();
    }
  }, [user]);

  const loadClients = async () => {
    if (!user) return;
    setIsLoadingClients(true);
    try {
      const clientsRef = collection(db, "consultantClients");
      const clientsQuery = query(
        clientsRef,
        where("consultantId", "==", user.email),
        orderBy("addedAt", "desc")
      );
      const snapshot = await getDocs(clientsQuery);
      const clientsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Client[];
      setClients(clientsData);
    } catch (error) {
      console.error("Error loading clients:", error);
    } finally {
      setIsLoadingClients(false);
    }
  };

  const handleSelectTemplate = (template: MandateTemplate) => {
    setSelectedTemplate(template);
    setFormData({
      ...formData,
      title: template.name,
      description: template.description,
      type: template.category,
      priority: template.defaultPriority,
      duration: template.defaultDuration,
      estimatedHours: template.defaultEstimatedHours || 40,
    });
    setActiveTab("details");
  };

  const filteredTemplates = selectedCategory === "all"
    ? MANDATE_TEMPLATES
    : MANDATE_TEMPLATES.filter(t => t.category === selectedCategory);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.title.trim()) newErrors.title = t.required;
    if (!formData.clientId) newErrors.clientId = t.required;
    if (!formData.type) newErrors.type = t.required;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm() || !user) return;

    setIsSubmitting(true);

    try {
      const selectedClient = clients.find(c => c.id === formData.clientId);
      const startDate = new Date(formData.startDate);
      const dueDate = new Date(startDate);
      dueDate.setDate(dueDate.getDate() + formData.duration);

      // Create milestones from template
      const milestones = selectedTemplate?.defaultMilestones.map((m, index) => {
        const milestoneDate = new Date(startDate);
        milestoneDate.setDate(milestoneDate.getDate() + m.dayOffset);
        return {
          id: `milestone-${index + 1}`,
          title: m.title,
          description: m.description,
          dueDate: Timestamp.fromDate(milestoneDate),
          completed: false,
        };
      }) || [];

      await addDoc(collection(db, "consultantMandates"), {
        consultantId: user.email,
        clientId: formData.clientId,
        clientName: selectedClient?.name || '',
        templateId: selectedTemplate?.id || null,
        title: formData.title,
        description: formData.description,
        type: formData.type,
        status: 'pending',
        priority: formData.priority,
        startDate: Timestamp.fromDate(startDate),
        dueDate: Timestamp.fromDate(dueDate),
        hourlyRate: formData.hourlyRate,
        estimatedHours: formData.estimatedHours,
        hoursWorked: 0,
        totalFee: formData.hourlyRate * formData.estimatedHours,
        currency: 'USD',
        milestones,
        documents: [],
        requiredDocuments: selectedTemplate?.requiredDocuments || [],
        deliverables: selectedTemplate?.deliverables || [],
        checklist: selectedTemplate?.checklist || [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      toast({
        title: t.success,
        description: t.successMessage,
      });

      navigate("/dashboard/consultant");
    } catch (error) {
      console.error("Error creating mandate:", error);
      toast({
        title: t.error,
        description: t.errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getCategoryLabel = (category: string) => {
    const key = category as keyof typeof translations.en;
    return t[key] || category;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="h-16 border-b border-border bg-card px-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard/consultant")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t.back}
          </Button>
        </div>
        <LanguageSelector />
      </header>

      {/* Content */}
      <main className="max-w-6xl mx-auto p-6 space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <Briefcase className="h-8 w-8 text-primary" />
            {t.title}
          </h1>
          <p className="text-muted-foreground mt-2">{t.subtitle}</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="templates">{t.selectTemplate}</TabsTrigger>
            <TabsTrigger value="details">{t.mandateDetails}</TabsTrigger>
          </TabsList>

          {/* Templates Tab */}
          <TabsContent value="templates" className="space-y-6">
            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              <Button
                variant={selectedCategory === "all" ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedCategory("all")}
              >
                {t.allTemplates}
              </Button>
              {Object.entries(CATEGORY_ICONS).map(([category, Icon]) => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(category)}
                >
                  <Icon className="mr-2 h-4 w-4" />
                  {getCategoryLabel(category)}
                </Button>
              ))}
            </div>

            {/* Template Cards */}
            <div className="grid gap-4 md:grid-cols-2">
              {filteredTemplates.map((template) => {
                const Icon = CATEGORY_ICONS[template.category];
                return (
                  <Card key={template.id} className="hover:shadow-lg transition-shadow cursor-pointer">
                    <CardHeader>
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-primary/10">
                          <Icon className="h-6 w-6 text-primary" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">{template.name}</CardTitle>
                          <CardDescription>{template.description}</CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <span>{template.defaultDuration} {t.days}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-muted-foreground" />
                          <span>{template.defaultEstimatedHours}h</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-muted-foreground" />
                          <span>{template.defaultMilestones.length} {t.milestones}</span>
                        </div>
                      </div>

                      <div>
                        <p className="text-sm font-medium mb-2">{t.deliverables}:</p>
                        <div className="flex flex-wrap gap-1">
                          {template.deliverables.slice(0, 3).map((d, i) => (
                            <Badge key={i} variant="secondary" className="text-xs">
                              {d}
                            </Badge>
                          ))}
                          {template.deliverables.length > 3 && (
                            <Badge variant="outline" className="text-xs">
                              +{template.deliverables.length - 3}
                            </Badge>
                          )}
                        </div>
                      </div>

                      <Button
                        className="w-full"
                        onClick={() => handleSelectTemplate(template)}
                      >
                        {t.useTemplate}
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}

              {/* Custom Mandate Card */}
              <Card className="border-dashed hover:shadow-lg transition-shadow cursor-pointer">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-muted">
                      <FileText className="h-6 w-6 text-muted-foreground" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{t.customMandate}</CardTitle>
                      <CardDescription>
                        {language === 'fr' ? 'Créez un mandat personnalisé sans modèle' : 'Create a custom mandate without template'}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      setSelectedTemplate(null);
                      setActiveTab("details");
                    }}
                  >
                    {language === 'fr' ? 'Commencer' : 'Get Started'}
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Mandate Details Tab */}
          <TabsContent value="details" className="space-y-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid gap-6 lg:grid-cols-3">
                {/* Main Form */}
                <div className="lg:col-span-2 space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>{t.mandateDetails}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="title">{t.mandateTitle} *</Label>
                        <Input
                          id="title"
                          placeholder={t.titlePlaceholder}
                          value={formData.title}
                          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                          className={errors.title ? "border-destructive" : ""}
                        />
                        {errors.title && <p className="text-sm text-destructive">{errors.title}</p>}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="description">{t.description}</Label>
                        <Textarea
                          id="description"
                          placeholder={t.descriptionPlaceholder}
                          value={formData.description}
                          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                          rows={4}
                        />
                      </div>

                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="clientId">{t.selectClient} *</Label>
                          <Select
                            value={formData.clientId}
                            onValueChange={(value) => setFormData({ ...formData, clientId: value })}
                          >
                            <SelectTrigger className={errors.clientId ? "border-destructive" : ""}>
                              <SelectValue placeholder={t.selectClient} />
                            </SelectTrigger>
                            <SelectContent>
                              {clients.length === 0 ? (
                                <SelectItem value="none" disabled>
                                  {t.noClients}
                                </SelectItem>
                              ) : (
                                clients.map((client) => (
                                  <SelectItem key={client.id} value={client.id}>
                                    {client.name} ({client.company || client.type})
                                  </SelectItem>
                                ))
                              )}
                            </SelectContent>
                          </Select>
                          {errors.clientId && <p className="text-sm text-destructive">{errors.clientId}</p>}
                          {clients.length === 0 && !isLoadingClients && (
                            <Button
                              type="button"
                              variant="link"
                              size="sm"
                              className="px-0"
                              onClick={() => navigate("/consultant/add-client")}
                            >
                              {t.addClientFirst}
                            </Button>
                          )}
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="type">{t.mandateType} *</Label>
                          <Select
                            value={formData.type}
                            onValueChange={(value: typeof formData.type) => setFormData({ ...formData, type: value })}
                          >
                            <SelectTrigger className={errors.type ? "border-destructive" : ""}>
                              <SelectValue placeholder={t.mandateType} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="legal">{t.legal}</SelectItem>
                              <SelectItem value="tax">{t.tax}</SelectItem>
                              <SelectItem value="financial">{t.financial}</SelectItem>
                              <SelectItem value="immigration">{t.immigration}</SelectItem>
                              <SelectItem value="due_diligence">{t.due_diligence}</SelectItem>
                              <SelectItem value="other">{t.other}</SelectItem>
                            </SelectContent>
                          </Select>
                          {errors.type && <p className="text-sm text-destructive">{errors.type}</p>}
                        </div>
                      </div>

                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="priority">{t.priority}</Label>
                          <Select
                            value={formData.priority}
                            onValueChange={(value: typeof formData.priority) => setFormData({ ...formData, priority: value })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="low">{t.low}</SelectItem>
                              <SelectItem value="medium">{t.medium}</SelectItem>
                              <SelectItem value="high">{t.high}</SelectItem>
                              <SelectItem value="urgent">{t.urgent}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="startDate">{t.startDate}</Label>
                          <Input
                            id="startDate"
                            type="date"
                            value={formData.startDate}
                            onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                          />
                        </div>
                      </div>

                      <div className="grid gap-4 sm:grid-cols-3">
                        <div className="space-y-2">
                          <Label htmlFor="duration">{t.duration}</Label>
                          <Input
                            id="duration"
                            type="number"
                            min={1}
                            value={formData.duration}
                            onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) || 30 })}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="estimatedHours">{t.estimatedHours}</Label>
                          <Input
                            id="estimatedHours"
                            type="number"
                            min={1}
                            value={formData.estimatedHours}
                            onChange={(e) => setFormData({ ...formData, estimatedHours: parseInt(e.target.value) || 40 })}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="hourlyRate">{t.hourlyRate}</Label>
                          <Input
                            id="hourlyRate"
                            type="number"
                            min={0}
                            value={formData.hourlyRate}
                            onChange={(e) => setFormData({ ...formData, hourlyRate: parseInt(e.target.value) || 0 })}
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Submit Buttons */}
                  <div className="flex justify-end gap-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate("/dashboard/consultant")}
                    >
                      {t.cancel}
                    </Button>
                    <Button type="submit" disabled={isSubmitting}>
                      <Save className="mr-2 h-4 w-4" />
                      {isSubmitting ? t.creating : t.create}
                    </Button>
                  </div>
                </div>

                {/* Template Details Sidebar */}
                {selectedTemplate && (
                  <div className="space-y-4">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">{t.templateDetails}</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div>
                          <p className="text-sm font-medium mb-2">{t.requiredDocuments}</p>
                          <ul className="text-sm text-muted-foreground space-y-1">
                            {selectedTemplate.requiredDocuments.map((doc, i) => (
                              <li key={i} className="flex items-start gap-2">
                                <FileText className="h-4 w-4 mt-0.5 flex-shrink-0" />
                                {doc}
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div>
                          <p className="text-sm font-medium mb-2">{t.deliverables}</p>
                          <ul className="text-sm text-muted-foreground space-y-1">
                            {selectedTemplate.deliverables.map((d, i) => (
                              <li key={i} className="flex items-start gap-2">
                                <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-green-500" />
                                {d}
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div>
                          <p className="text-sm font-medium mb-2">{t.milestones}</p>
                          <ul className="text-sm text-muted-foreground space-y-2">
                            {selectedTemplate.defaultMilestones.map((m, i) => (
                              <li key={i} className="flex items-start gap-2">
                                <Calendar className="h-4 w-4 mt-0.5 flex-shrink-0" />
                                <div>
                                  <p className="font-medium">{m.title}</p>
                                  <p className="text-xs">Day {m.dayOffset}</p>
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </div>
            </form>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
